/* EuroSpeed vehicle-aware collection hero (2026-09-30, widened 2026-10-01).

   Jeff, reporting a real bug: picking "Audi R8" via the vehicle selector on a category page
   (e.g. /collections/carbon-fiber-skirt/audi-r8) still showed Volkswagen Golf cards in the
   top carousel. Root cause: es-collection-hero.liquid's "Shop by vehicle"/"Shop by category"
   strips are both static, baked at generation time (build_hero.py), with zero awareness of
   the tag filter actually applied to the page. The SAME static-content problem existed on
   vehicle collections too (every vehicle's "Shop by category" strip showed the identical,
   globally-static photo per category -- a BMW page's Exhausts card was a Golf exhaust photo).

   Fix: once the real product grid is on the page, this script rebuilds the hero carousel from
   the grid's OWN data -- same proven pattern as assets/es-search-strip.js (scan
   .product-item[data-tags], take the first clean photo per tag). It never invents data; if the
   grid doesn't have what's needed it leaves the static (already-correct-for-the-unfiltered-case)
   carousel exactly as rendered.

   A hero opts into "multi-category" rebuilding via `data-multi-category="true"` on #es-cat-hero
   (set by es-collection-hero.liquid for genuine multi-category pages: vehicle collections, and
   the carbon-fiber hub) and can override which tag prefix to scan via `data-tag-prefix`
   (defaults to "cat-"; the carbon-fiber hub uses "cf-" since carbon parts are bucketed by the
   finer-grained cf-* tags, not the coarse cat-carbon-fiber bucket).

   Two cases:
   1. multi-category=true: ALWAYS rebuilt from this page's own products (every vehicle collection
      gets its own real photos; the carbon-fiber hub shows its real 14-part lineup). If a vehicle
      tag filter is also active in the URL, the rebuild scans the already-filtered grid, so the
      cards narrow to only the categories/parts that vehicle actually has.
   2. Anything else (a single-category collection like carbon-fiber-skirt) WITH a vehicle tag
      filter active: there's no real "other categories" data to build from (it's one category by
      definition), so the existing static vehicle-card list is narrowed down to just the matching
      card(s) instead. No filter active -> left alone entirely.
*/
(function () {
  var MIN_CARDS = 3;
  var MIN_PRODUCTS = 2;
  var MIN_IMG = 300;

  /* Fallback labels for every governed cat-* bucket (category-reference.md) so a category missing from
     window.ES_CAT_ART (no baked art) still gets a card using a real product photo from the grid. */
  var CAT_LABELS = {"cat-bov-diverter": "Blow Off & Diverter Valves", "cat-boost-sensors": "Boost Taps & Sensors", "cat-braces-cages": "Chassis Braces & Cages", "cat-brakes": "Brakes, Pads & Rotors", "cat-carbon-fiber": "Carbon Fiber", "cat-catch-cans-pcv": "Catch Cans & PCV", "cat-clutch-flywheels": "Clutch Kits & Flywheels", "cat-coilovers-springs": "Coilovers & Springs", "cat-control-arms": "Control Arms & Bushings", "cat-ecu-tuning": "ECUs & Tuning", "cat-engine-internals": "Engine Internals", "cat-engine-mounts": "Engine & Drivetrain Mounts", "cat-exhaust": "Exhausts & Downpipes", "cat-exterior-accessories": "Exterior Accessories", "cat-fueling": "Fuel Systems", "cat-gauges": "Gauges & Pods", "cat-heat-management": "Heat Management", "cat-ignition": "Ignition & Spark Plugs", "cat-intakes": "Intakes & Filters", "cat-intercoolers": "Intercoolers & Piping", "cat-interior-accessories": "Interior Accessories", "cat-lighting": "Lighting", "cat-maintenance": "Maintenance & Service", "cat-oil-pans": "Oil Pans & Shields", "cat-oils-fluids": "Oils & Fluids", "cat-pulleys": "Pulleys", "cat-radiators-cooling": "Radiators & Cooling", "cat-shocks-struts": "Shocks & Struts", "cat-skirts-splitters-spoilers": "Skirts, Splitters & Spoilers", "cat-sway-bars": "Sway Bars & End Links", "cat-throttle-bodies": "Throttle Bodies & Manifolds", "cat-tools-hardware": "Tools & Hardware", "cat-transmission-shifters": "Transmission & Shifters", "cat-turbos-superchargers": "Turbos & Superchargers", "cat-water-meth-nitrous": "Water/Meth & Nitrous", "cat-wheel-spacers": "Wheel Spacers", "cat-wheels": "Wheels", "cat-decals-stickers": "Decals & Badges", "cat-apparel-merch": "Apparel & Merch", "cat-air-suspension": "Air Suspension"};
  function catLabel(t, FALLBACK) { return (FALLBACK[t] && FALLBACK[t].label) || CAT_LABELS[t] || null; }

  function usableImage(item) {
    var img = item.querySelector('.product-thumb img') || item.querySelector('img');
    if (!img) return null;
    var src = img.getAttribute('src') || '';
    if (!src || /no-image|placeholder|\.svg(\?|$)/i.test(src)) return null;
    var w = parseInt(img.getAttribute('width') || '0', 10);
    var h = parseInt(img.getAttribute('height') || '0', 10);
    if (w && w < MIN_IMG) return null;
    if (h && h < MIN_IMG) return null;
    if (w && h && (w / h > 2.2 || h / w > 2.2)) return null;
    return src.indexOf('//') === 0 ? window.location.protocol + src : src;
  }

  function imgAttrs(img, label) {
    var isCdn = /cdn\.shopify\.com|\/cdn\/shop\//.test(img);
    var base = img.replace(/([?&])width=\d+/, '$1width=600');
    var q = base.indexOf('?') > -1 ? '&' : '?';
    var src = isCdn && !/width=/.test(base) ? base + q + 'width=600' : base;
    return 'src="' + src + '" width="600" height="400" alt="' + label + '" loading="lazy" decoding="async"';
  }

  /* One pass over the rendered grid: per tag (matching the given prefix), product count + best
     (first usable) photo. */
  function scanByCategory(prefix) {
    var out = {};
    document.querySelectorAll('.product-item[data-tags]').forEach(function (el) {
      var tags = (el.getAttribute('data-tags') || '').split(',');
      var img = null, looked = false;
      tags.forEach(function (t) {
        t = t.trim();
        if (t.indexOf(prefix) !== 0) return;
        if (!out[t]) out[t] = { n: 0, img: null };
        out[t].n++;
        if (!out[t].img) {
          if (!looked) { img = usableImage(el); looked = true; }
          if (img) out[t].img = img;
        }
      });
    });
    return out;
  }

  function buildCard(href, img, label, cta) {
    return '<a class="es-crs-card" href="' + href + '"><span class="es-crs-imgwrap"><img '
      + imgAttrs(img, label) + '><span class="es-crs-scrim"></span><span class="es-crs-txt">'
      + '<span class="es-crs-label">' + label + '</span><span class="es-crs-cta">' + cta + ' &rarr;</span>'
      + '</span></span></a>';
  }

  /* Single-category collection + an active vehicle filter: there is no real "shop by category"
     data to build from (scanning the filtered grid yields just the one category already on
     screen). The only thing we can say reliably is which vehicle is active, so narrow the
     existing static vehicle list down to just the matching card(s) (never fabricate categories
     that don't exist in the page's own data). Jeff, 2026-09-30: keep it visible even down to one
     card rather than hiding -- the catalog is actively growing, so a thin strip today fills out
     on its own as more gets added; disappearing entirely reads as broken. */
  function narrowToActiveVehicle(track, activeFilterPath) {
    // Match on visible text (label + image alt), not the href -- another script on the page
    // (vehicle-pills.js) already rewrites these cards' links to short "/<collection>/<tag>"
    // slugs before this runs, so comparing against the href's own handle is unreliable.
    var words = activeFilterPath.split('/').pop().toLowerCase().split(/[-+]/).filter(function (w) {
      return w.length > 1; // drop single letters/numbers that would match almost anything
    });
    if (!words.length) return false;
    var cards = [].slice.call(track.querySelectorAll('.es-crs-card'));
    if (!cards.length) return false;
    var kept = cards.filter(function (c) {
      var label = c.querySelector('.es-crs-label') ? c.querySelector('.es-crs-label').textContent : '';
      var alt = c.querySelector('img') ? c.querySelector('img').getAttribute('alt') || '' : '';
      var hay = (label + ' ' + alt).toLowerCase();
      return words.every(function (w) { return hay.indexOf(w) !== -1; });
    });
    // de-dupe (the carousel engine clones cards for the infinite-loop effect)
    var seen = {}, unique = [];
    kept.forEach(function (c) {
      var href = c.getAttribute('href');
      if (seen[href]) return;
      seen[href] = 1;
      unique.push(c);
    });
    if (!unique.length) return false; // couldn't confidently match -- leave the static list alone
    track.innerHTML = '';
    unique.forEach(function (c) { track.appendChild(c); });
    if (window.esCarouselInit) window.esCarouselInit();
    return true;
  }

  function run() {
    var hero = document.getElementById('es-cat-hero');
    var track = document.getElementById('es-hero-track');
    var titleEl = document.getElementById('es-hero-title');
    if (!hero || !track || !titleEl) return;

    var FALLBACK = window.ES_CAT_ART || {};
    var isMultiCategory = hero.getAttribute('data-multi-category') === 'true';
    var prefix = hero.getAttribute('data-tag-prefix') || 'cat-';
    // Collections with no entry in the hand-maintained hero_lists render this container with an
    // EMPTY track and no server-side fallback cards at all -- the only content it will ever have
    // is whatever this scan builds. A collection that already has real baked-in cards (the carousel
    // collections, the carbon-fiber hub) always starts with content, so this flag only ever fires
    // for the no-data-at-all case, and only there does "nothing resolved" mean "hide the shell"
    // rather than "leave the good static content alone".
    var hadStaticContent = track.children.length > 0;

    var parts = window.location.pathname.split('/').filter(Boolean);
    // parts[0] === 'collections', parts[1] === handle, parts[2+] === active tag filter (if any)
    var activeFilterPath = (parts[0] === 'collections' && parts.length > 2) ? parts.slice(2).join('/') : null;

    if (!isMultiCategory) {
      if (activeFilterPath) narrowToActiveVehicle(track, activeFilterPath);
      verifyCards(track);
      return; // no filter active: the existing static vehicle list is already accurate, leave it
    }

    // Multi-category page (a vehicle collection, the carbon-fiber hub, or an otherwise-unmapped
    // collection with no hero_lists entry): always rebuild from this page's own real products --
    // never the globally-static set baked at generation time. If a vehicle filter is active, the
    // grid is already scoped to it, so the rebuild naturally narrows to only the categories/parts
    // that vehicle actually has.
    var found = scanByCategory(prefix);
    function pickRows(minProducts) {
      return Object.keys(found)
        .filter(function (t) { return found[t].n >= minProducts && (prefix !== 'cat-' ? FALLBACK[t] && FALLBACK[t].label : catLabel(t, FALLBACK)) && (found[t].img || (FALLBACK[t] && FALLBACK[t].img)); })
        .sort(function (a, b) { return found[b].n - found[a].n; })
        .slice(0, 14);
    }
    var rows = pickRows(MIN_PRODUCTS);
    // A thin vehicle page (a few dozen products) may have no category with 2+ hits. Never hide the
    // strip for that (STANDING-DECISIONS 28): fall back to single-product categories.
    if (rows.length < MIN_CARDS && activeFilterPath) rows = pickRows(1);
    if (rows.length < (activeFilterPath ? 1 : MIN_CARDS)) { if (!hadStaticContent) hero.style.display = 'none'; else verifyCards(track); return; }

    var basePath = window.location.pathname.replace(/\/$/, '');
    var cards = rows.map(function (t) {
      var img = found[t].img || (FALLBACK[t] && FALLBACK[t].img);
      if (!img) return '';
      var label = catLabel(t, FALLBACK) || (FALLBACK[t] && FALLBACK[t].label);
      // Shopify's tag route is /collections/<handle>/<tag1>+<tag2>: a SECOND path segment 404s.
      // When a vehicle filter is already in the path, AND the category tag on with "+", never "/".
      var sep = activeFilterPath ? '+' : '/';
      return buildCard(basePath + sep + encodeURIComponent(t), img, label, 'Shop ' + label);
    }).join('');
    if (!cards) { if (!hadStaticContent) hero.style.display = 'none'; return; }

    track.innerHTML = cards;
    if (window.esCarouselInit) window.esCarouselInit();
    verifyCards(track);
  }

  /* Safety net (2026-10-21, after the GTI MK7 404; widened same day): every collection-hero card
     is fetched once and dropped if its target is not a 200 whose grid (#es-grid .product-item)
     holds at least one product. Covers rebuilt cards AND the server-rendered static ones (catalog
     changes can empty a static card's target at any time). Verdicts are cached in sessionStorage
     for 30 min so a page view costs no extra requests once warm. Cards are only ever REMOVED; if
     every card would go, nothing is removed and the strip is never hidden (STANDING-DECISIONS 28).
     A failed fetch (network) keeps the card. */
  var TTL = 30 * 60 * 1000;
  function cached(href) {
    try {
      var v = JSON.parse(sessionStorage.getItem('es-hv:' + href) || 'null');
      if (v && Date.now() - v.t < TTL) return v.ok;
    } catch (_) {}
    return null;
  }
  function remember(href, ok) {
    try { sessionStorage.setItem('es-hv:' + href, JSON.stringify({ ok: ok, t: Date.now() })); } catch (_) {}
  }
  function liveCheck(href) {
    var c = cached(href);
    if (c !== null) return Promise.resolve(c);
    return fetch(href, { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) return false;
      return r.text().then(function (t) {
        var ok = new DOMParser().parseFromString(t, 'text/html').querySelector('#es-grid .product-item') !== null;
        remember(href, ok);
        return ok;
      });
    }).catch(function () { return true; });
  }
  function verifyCards(track) {
    var cards = [].slice.call(track.querySelectorAll('.es-crs-card'));
    var hrefs = [], seen = {};
    cards.forEach(function (c) {
      var h = c.getAttribute('href');
      if (h && h.indexOf('/collections/') === 0 && !seen[h]) { seen[h] = 1; hrefs.push(h); }
    });
    var verdict = {}, i = 0;
    function worker() {
      if (i >= hrefs.length) return Promise.resolve();
      var h = hrefs[i++];
      return liveCheck(h).then(function (ok) { verdict[h] = ok; return worker(); });
    }
    Promise.all([worker(), worker(), worker()]).then(function () {
      var bad = cards.filter(function (c) { return verdict[c.getAttribute('href')] === false; });
      var good = cards.length - bad.length;
      if (!bad.length || !good) return;
      bad.forEach(function (c) { c.parentNode && c.parentNode.removeChild(c); });
      if (window.esCarouselInit) window.esCarouselInit();
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
})();
